import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Plus, Save } from "lucide-react";
import { Fragment, useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { nanoid } from "nanoid";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Dialog } from "../../base/dialog/dialog";
import { DialogShell } from "../dialog-shell/DialogShell";
import { DialogShellHeader } from "../dialog-shell/DialogShellHeader";
import { DialogShellBody } from "../dialog-shell/DialogShellBody";
import { DialogShellFooter } from "../dialog-shell/DialogShellFooter";
import { UnsavedChangesConfirmDialog } from "../unsaved-changes-confirm-dialog/unsaved-changes-confirm-dialog";
import { buildEmptyRuleInputs, getDecisionTableColumns, reconcileSharedOutputSuccessors, reorderRulesByClientId } from "./decisionTableColumns";
import { useFormDialogWithUnsavedChanges } from "../../../hooks/useFormDialogWithUnsavedChanges";
import { useReorderSensors } from "../../../hooks/useReorderSensors";
import { DecisionTableHeader } from "./DecisionTableHeader";
import { DecisionTableRow } from "./DecisionTableRow";
/** Adapt the editor's column objects to the shared blank-inputs builder. */ const buildEmptyInputs = (columns)=>buildEmptyRuleInputs(columns.map((col)=>col.name));
export function DecisionTableDialog({ onClose, onSave, initialData = [] }) {
    const { t } = useTranslation();
    const [validationError, setValidationError] = useState(null);
    const [invalidRules, setInvalidRules] = useState(new Set());
    // Initialize columns from initialData or with defaults
    // Since parent only mounts this component when open, this runs once with fresh data
    const [columns, setColumns] = useState(()=>{
        if (initialData.length > 0) {
            const columnNames = getDecisionTableColumns(initialData);
            return columnNames.map((key, index)=>({
                    id: `col-${index}`,
                    name: key
                }));
        }
        return [
            {
                id: "col-0",
                name: t("viralRecording.modals.decisionTable.defaultCondition", {
                    number: 1
                })
            }
        ];
    });
    // Initialize rules from initialData or with empty defaults
    const [rules, setRules] = useState(()=>{
        if (initialData.length > 0) {
            return initialData.map((rule)=>({
                    _id: rule._id,
                    clientId: nanoid(),
                    inputs: rule.inputs ?? [],
                    output: rule.output || ""
                }));
        }
        const emptyInputs = buildEmptyInputs(columns);
        return [
            {
                clientId: nanoid(),
                inputs: emptyInputs,
                output: ""
            }
        ];
    });
    const sensors = useReorderSensors();
    const handleDragEnd = useCallback((event)=>{
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setRules((prev)=>reorderRulesByClientId(prev, String(active.id), String(over.id)));
    }, []);
    // Capture initial state once on mount for dirty detection
    const [initialState] = useState(()=>({
            columns: JSON.parse(JSON.stringify(columns)),
            rules: JSON.parse(JSON.stringify(rules))
        }));
    // Compute isDirty by comparing current state with initial state
    const isDirty = useMemo(()=>{
        const currentState = {
            columns,
            rules
        };
        return JSON.stringify(currentState) !== JSON.stringify(initialState);
    }, [
        columns,
        rules,
        initialState
    ]);
    // Total table columns: drag handle + rule number + output + actions +
    // (condition input + spacer) per column
    const totalColSpan = useMemo(()=>4 + columns.length * 2, [
        columns.length
    ]);
    // Use unsaved changes hook
    const { showCloseConfirmation, handleOpenChange, handleDiscardChanges, handleStay } = useFormDialogWithUnsavedChanges({
        isDirty,
        onClose
    });
    const handleAddColumn = useCallback((afterIndex)=>{
        const newColumn = {
            id: `col-${Date.now()}`,
            name: t("viralRecording.modals.decisionTable.defaultCondition", {
                number: columns.length + 1
            })
        };
        const newColumns = afterIndex !== undefined ? [
            ...columns.slice(0, afterIndex + 1),
            newColumn,
            ...columns.slice(afterIndex + 1)
        ] : [
            ...columns,
            newColumn
        ];
        setColumns(newColumns);
        setRules(rules.map((rule)=>({
                ...rule,
                inputs: [
                    ...rule.inputs,
                    {
                        column: newColumn.name,
                        value: ""
                    }
                ]
            })));
    }, [
        columns,
        rules,
        t
    ]);
    const handleDeleteColumn = useCallback((columnId)=>{
        const column = columns.find((col)=>col.id === columnId);
        if (!column || columns.length <= 1) return;
        setColumns(columns.filter((col)=>col.id !== columnId));
        setRules(rules.map((rule)=>({
                ...rule,
                inputs: rule.inputs.filter((input)=>input.column !== column.name)
            })));
    }, [
        columns,
        rules
    ]);
    const handleStartEditColumn = useCallback((columnId)=>{
        setColumns(columns.map((col)=>col.id === columnId ? {
                ...col,
                isEditing: true
            } : col));
    }, [
        columns
    ]);
    const handleSaveColumnName = useCallback((columnId, newName)=>{
        const column = columns.find((col)=>col.id === columnId);
        if (!column || !newName.trim()) {
            setColumns(columns.map((col)=>col.id === columnId ? {
                    ...col,
                    isEditing: false
                } : col));
            return;
        }
        const oldName = column.name;
        const trimmedNewName = newName.trim();
        // The column name is the cell key for reads/writes, so a duplicate would
        // merge two columns (getRuleInputValue reads the first match,
        // handleUpdateRuleInput writes every match). Reject a colliding rename —
        // just close the editor, keeping the old name.
        const collides = columns.some((col)=>col.id !== columnId && col.name === trimmedNewName);
        if (collides) {
            setColumns(columns.map((col)=>col.id === columnId ? {
                    ...col,
                    isEditing: false
                } : col));
            return;
        }
        setColumns(columns.map((col)=>col.id === columnId ? {
                ...col,
                name: trimmedNewName,
                isEditing: false
            } : col));
        setRules(rules.map((rule)=>({
                ...rule,
                inputs: rule.inputs.map((input)=>input.column === oldName ? {
                        ...input,
                        column: trimmedNewName
                    } : input)
            })));
    }, [
        columns,
        rules
    ]);
    const handleCancelEditColumn = useCallback((columnId)=>{
        setColumns(columns.map((col)=>col.id === columnId ? {
                ...col,
                isEditing: false
            } : col));
    }, [
        columns
    ]);
    const handleAddRule = useCallback(()=>{
        const emptyInputs = buildEmptyInputs(columns);
        setRules([
            ...rules,
            {
                clientId: nanoid(),
                inputs: emptyInputs,
                output: ""
            }
        ]);
    }, [
        columns,
        rules
    ]);
    const handleDeleteRule = useCallback((index)=>{
        if (rules.length > 1) setRules(rules.filter((_, i)=>i !== index));
    }, [
        rules
    ]);
    // Helper to clear validation error for a specific rule
    const clearRuleError = useCallback((ruleIndex)=>{
        if (invalidRules.has(ruleIndex)) {
            const newInvalidRules = new Set(invalidRules);
            newInvalidRules.delete(ruleIndex);
            setInvalidRules(newInvalidRules);
            if (newInvalidRules.size === 0) setValidationError(null);
        }
    }, [
        invalidRules
    ]);
    const handleUpdateRuleInput = useCallback((ruleIndex, columnName, value)=>{
        setRules(rules.map((rule, i)=>{
            if (i !== ruleIndex) return rule;
            const hasColumn = rule.inputs.some((input)=>input.column === columnName);
            const inputs = hasColumn ? rule.inputs.map((input)=>input.column === columnName ? {
                    ...input,
                    value
                } : input) : [
                ...rule.inputs,
                {
                    column: columnName,
                    value
                }
            ];
            return {
                ...rule,
                inputs
            };
        }));
        clearRuleError(ruleIndex);
    }, [
        rules,
        clearRuleError
    ]);
    const handleUpdateRuleOutput = useCallback((ruleIndex, value)=>{
        setRules(rules.map((rule, i)=>i === ruleIndex ? {
                ...rule,
                output: value
            } : rule));
        clearRuleError(ruleIndex);
    }, [
        rules,
        clearRuleError
    ]);
    const handleSave = useCallback(()=>{
        const invalidRuleIndices = new Set();
        rules.forEach((rule, index)=>{
            if (!rule.inputs.some((input)=>input.value?.trim()) || !rule.output?.trim()) invalidRuleIndices.add(index);
        });
        if (invalidRuleIndices.size > 0) {
            setInvalidRules(invalidRuleIndices);
            setValidationError(t("viralRecording.modals.decisionTable.validationMessageNew"));
            return;
        }
        setValidationError(null);
        setInvalidRules(new Set());
        const perRuleSuccessors = rules.map((rule)=>{
            const existing = rule._id ? initialData.find((r)=>r._id === rule._id)?.successors : undefined;
            return {
                _id: rule._id,
                inputs: rule.inputs,
                output: rule.output,
                successors: existing ?? {
                    persons: [],
                    orgFunctions: [],
                    systems: [],
                    externalContacts: []
                }
            };
        });
        // Preserving per-`_id` successors (above) survives reorders, but can
        // leave siblings divergent when a rule's output is renamed to match
        // another — reconcile so siblings converge on the group's successors.
        const formattedRules = reconcileSharedOutputSuccessors(perRuleSuccessors);
        onSave(formattedRules);
        onClose();
    }, [
        rules,
        initialData,
        onSave,
        onClose,
        t
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Dialog, {
                open: true,
                onOpenChange: handleOpenChange,
                children: /*#__PURE__*/ _jsxs(DialogShell, {
                    size: "xl",
                    minHeight: 800,
                    children: [
                        /*#__PURE__*/ _jsx(DialogShellHeader, {
                            title: t("viralRecording.modals.decisionTable.title"),
                            subtitle: t("viralRecording.modals.decisionTable.descriptionNew"),
                            onClose: ()=>handleOpenChange(false)
                        }),
                        /*#__PURE__*/ _jsxs(DialogShellBody, {
                            children: [
                                validationError && /*#__PURE__*/ _jsx("div", {
                                    className: "px-4 py-3 text-sm text-danger border border-danger-line rounded-lg bg-danger-surface",
                                    children: validationError
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col flex-1 min-h-0 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "flex-1 min-h-0 overflow-auto border border-line rounded-lg shadow-xs",
                                            children: /*#__PURE__*/ _jsx(DndContext, {
                                                sensors: sensors,
                                                collisionDetection: closestCenter,
                                                onDragEnd: handleDragEnd,
                                                children: /*#__PURE__*/ _jsxs("table", {
                                                    className: "w-full border-collapse",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(DecisionTableHeader, {
                                                            columns: columns,
                                                            onStartEditColumn: handleStartEditColumn,
                                                            onSaveColumnName: handleSaveColumnName,
                                                            onCancelEditColumn: handleCancelEditColumn,
                                                            onDeleteColumn: handleDeleteColumn,
                                                            onAddColumn: handleAddColumn
                                                        }),
                                                        /*#__PURE__*/ _jsxs("tbody", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(SortableContext, {
                                                                    items: rules.map((rule)=>rule.clientId),
                                                                    strategy: verticalListSortingStrategy,
                                                                    children: rules.map((rule, ruleIndex)=>/*#__PURE__*/ _jsxs(Fragment, {
                                                                            children: [
                                                                                ruleIndex > 0 && /*#__PURE__*/ _jsx("tr", {
                                                                                    "aria-hidden": "true",
                                                                                    children: /*#__PURE__*/ _jsx("td", {
                                                                                        colSpan: totalColSpan,
                                                                                        className: "py-0",
                                                                                        children: /*#__PURE__*/ _jsxs("div", {
                                                                                            className: "relative flex items-center",
                                                                                            children: [
                                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                                    className: "flex-1 border-t border-line"
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("span", {
                                                                                                    className: "px-3 text-xs font-semibold tracking-wider text-fg-subtle uppercase",
                                                                                                    children: t("viralRecording.modals.decisionTable.orSeparator")
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                                    className: "flex-1 border-t border-line"
                                                                                                })
                                                                                            ]
                                                                                        })
                                                                                    })
                                                                                }),
                                                                                /*#__PURE__*/ _jsx(DecisionTableRow, {
                                                                                    rule: rule,
                                                                                    ruleIndex: ruleIndex,
                                                                                    columns: columns,
                                                                                    allRules: rules,
                                                                                    invalidRules: invalidRules,
                                                                                    canDelete: rules.length > 1,
                                                                                    onUpdateRuleInput: handleUpdateRuleInput,
                                                                                    onUpdateRuleOutput: handleUpdateRuleOutput,
                                                                                    onDeleteRule: handleDeleteRule
                                                                                })
                                                                            ]
                                                                        }, rule.clientId))
                                                                }),
                                                                /*#__PURE__*/ _jsx("tr", {
                                                                    onClick: handleAddRule,
                                                                    className: "cursor-pointer group",
                                                                    children: /*#__PURE__*/ _jsx("td", {
                                                                        colSpan: totalColSpan,
                                                                        className: "py-2",
                                                                        children: /*#__PURE__*/ _jsxs("div", {
                                                                            className: "relative flex items-center",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                    className: "flex-1 border-t border-line group-hover:border-info-line"
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("span", {
                                                                                    className: "flex items-center gap-1.5 px-3 text-xs font-semibold tracking-wider text-fg-subtle uppercase transition-colors group-hover:text-info",
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx(Plus, {
                                                                                            className: "w-3.5 h-3.5"
                                                                                        }),
                                                                                        t("viralRecording.buttons.addRule")
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                    className: "flex-1 border-t border-line group-hover:border-info-line"
                                                                                })
                                                                            ]
                                                                        })
                                                                    })
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "px-4 py-3 text-sm text-info border border-info-line rounded-lg bg-info-surface",
                                            children: t("viralRecording.modals.decisionTable.successorReminder")
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(DialogShellFooter, {
                            secondary: {
                                label: t("viralRecording.buttons.back"),
                                onClick: ()=>handleOpenChange(false)
                            },
                            primary: {
                                label: t("viralRecording.buttons.save"),
                                onClick: handleSave,
                                suffix: /*#__PURE__*/ _jsx(Save, {
                                    className: "w-4 h-4"
                                })
                            }
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(UnsavedChangesConfirmDialog, {
                open: showCloseConfirmation,
                onStay: handleStay,
                onDiscard: handleDiscardChanges
            })
        ]
    });
}

//# sourceMappingURL=DecisionTableDialog.js.map